import { useCallback, useEffect, useState } from "react";
import { NavLink, Navigate, Route, BrowserRouter as Router, Routes } from "react-router-dom";
import { auth } from "./api/client";
import OfflineBanner from "./components/OfflineBanner";
import { t } from "./i18n/en";
import Alerts from "./pages/Alerts";
import Login from "./pages/Login";
import Radar from "./pages/Radar";
import Settings from "./pages/Settings";
import Types from "./pages/Types";
import { useLivePositionReporting } from "./settings/useSettings";

type Session = { username: string } | null;

function Shell({ username, onSignOut }: { username: string; onSignOut: () => void }) {
  // Keeps 'auto' and 'live' alert rules anchored to where you actually are, for as long as the
  // app is open (PLAN D4).
  useLivePositionReporting(true);

  return (
    <div className="shell">
      <OfflineBanner />
      <div className="shell-body">
        <Routes>
          <Route path="/" element={<Radar />} />
          <Route path="/alerts" element={<Alerts />} />
          <Route path="/types" element={<Types />} />
          <Route path="/settings" element={<Settings username={username} onSignOut={onSignOut} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
      <nav className="tabs">
        <NavLink to="/" end>
          <span aria-hidden>◎</span>
          {t.nav.radar}
        </NavLink>
        <NavLink to="/alerts">
          <span aria-hidden>△</span>
          {t.nav.alerts}
        </NavLink>
        <NavLink to="/settings">
          <span aria-hidden>⚙</span>
          {t.nav.settings}
        </NavLink>
      </nav>
    </div>
  );
}

export default function App() {
  // `undefined` = still asking the server, distinct from `null` = asked, nobody home. Without
  // that distinction the login form flashes on every cold launch before /auth/status answers,
  // which on a home-screen PWA is the first thing you see every single time.
  const [session, setSession] = useState<Session | undefined>(undefined);

  useEffect(() => {
    auth
      .status()
      .then((s) => setSession(s.authenticated && s.username ? { username: s.username } : null))
      .catch(() => setSession(null));
  }, []);

  const signOut = useCallback(async () => {
    try {
      await auth.logout();
    } finally {
      setSession(null);
    }
  }, []);

  if (session === undefined) {
    return (
      <div className="screen centered">
        <p className="dim">{t.loading}</p>
      </div>
    );
  }

  if (session === null) {
    return <Login onSignedIn={(username) => setSession({ username })} />;
  }

  return (
    <Router>
      <Shell username={session.username} onSignOut={signOut} />
    </Router>
  );
}
